import { useCallback } from "react"

import { useTranslation } from "react-i18next"

import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

import { useBindingForPath, useIsBindingsRefreshing } from "./bindings-context"
import { TileBindingBadge } from "./tile-binding-badge"
import { useTileRefMap } from "./tile-ref-context"

export function TileWrapper({
  children,
  elementPath,
  elementType,
  index,
  isSelected,
  onSelect,
  tileLabel,
}: {
  children: React.ReactNode
  elementPath?: string
  /**
   * The wrapped element's type. Used by TileBindingBadge to suppress
   * the refresh-state pill on atomic widgets / containers where it
   * makes no sense (Heading, Checkbox, Card, …).
   */
  elementType?: string
  index: number | undefined
  isSelected: boolean
  onSelect: ((index: number) => void) | undefined
  /** Tile title/label (or element type) used to give the select button a unique accessible name. */
  tileLabel?: string
}) {
  const { t } = useTranslation()
  const refMap = useTileRefMap()

  // Skeleton-overlay condition: a refresh is running AND this tile has a
  // binding. Bound tiles get fresh data from the SQL re-run; unbound tiles
  // wait for the AI rebuild (a different, slower path) and showing the
  // skeleton there would be misleading about how soon they'll update.
  const hasBinding = useBindingForPath(elementPath) !== undefined
  const isRefreshing = useIsBindingsRefreshing()
  const showSkeleton = hasBinding && isRefreshing

  const callbackRef = useCallback(
    (el: HTMLDivElement | null) => {
      if (index === undefined || !refMap) return
      if (el) refMap.set(index, el)
      else refMap.delete(index)
    },
    [index, refMap]
  )

  // §2.4 — devtools picker maps DOM nodes back to spec keys via this
  // attribute. Production HTML carries the attr unconditionally; it's
  // a no-op outside dev sessions and lets the panel highlight the
  // matching tile when an admin clicks "pick".
  const pickerAttrs = elementPath ? { "data-dash-key": elementPath } : {}

  if (index === undefined || !onSelect) {
    return (
      <div className="relative" {...pickerAttrs}>
        <TileBindingBadge elementPath={elementPath} elementType={elementType} />
        {children}
        {showSkeleton && <SkeletonOverlay />}
      </div>
    )
  }

  return (
    <div
      ref={callbackRef}
      role="presentation"
      {...pickerAttrs}
      className={cn(
        "relative cursor-pointer rounded-lg transition-shadow",
        isSelected
          ? "ring-primary ring-2 ring-offset-2"
          : "hover:ring-muted-foreground/30 hover:ring-1 hover:ring-offset-1"
      )}
      onClick={(e) => {
        e.stopPropagation()
        onSelect(isSelected ? -1 : index)
      }}
    >
      {/* Keyboard route to the same select/deselect action as clicking the tile. */}
      <button
        aria-pressed={isSelected}
        type="button"
        className={cn(
          "sr-only focus-visible:not-sr-only focus-visible:absolute focus-visible:top-1 focus-visible:left-1 focus-visible:z-20 focus-visible:px-2 focus-visible:py-1",
          "bg-background text-foreground focus-visible:ring-ring rounded-md border px-2 py-1 text-xs focus-visible:ring-2 focus-visible:outline-none"
        )}
        onClick={(e) => {
          e.stopPropagation()
          onSelect(isSelected ? -1 : index)
        }}
      >
        {t("dash.dashboard.select_tile", {
          defaultValue: "Select tile: {{name}}",
          name: `${tileLabel ?? elementType ?? ""} #${index + 1}`.trim(),
        })}
      </button>
      <TileBindingBadge elementPath={elementPath} elementType={elementType} />
      {children}
      {showSkeleton && <SkeletonOverlay />}
    </div>
  )
}

/**
 * Translucent skeleton layer covering a tile during binding refresh.
 * `pointer-events-none` keeps the underlying tile still interactive, so
 * a user clicking on a half-refreshed tile still toggles selection /
 * triggers any registered actions.
 */
function SkeletonOverlay() {
  return (
    <div
      aria-busy="true"
      aria-label="Refreshing tile data"
      className="bg-background/60 pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg backdrop-blur-[1px]"
    >
      <Skeleton className="h-3/4 w-3/4" />
    </div>
  )
}
